import React from 'react'
import {
  List,
  useTable,
  ShowButton,
  EditButton,
  DateField,
} from '@refinedev/antd'
import { useList, useNavigation } from '@refinedev/core'
import { Table, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { renderStatus, renderAvatarName } from '../../theme/columns'
import { ListPage, TableSurface, type Stat } from '../../theme/listkit'
import { CalendarCheck, CalendarDays, Ticket, UsersRound } from '../../components/icons'

type Rsvp = Tables<'rsvps'>
type Profile = Tables<'profiles'>
type Event = Tables<'events'>

// Local shape for the one-row admin summary view. We don't use Tables<'…'>
// here because the generated view Row types it as `id: number | null`, which
// fails Refine's BaseRecord constraint (id must be a non-null BaseKey).
interface RsvpsSummary {
  id: number
  confirmed_count: number | null
  pending_count: number | null
  waitlisted_count: number | null
  total_guests: number | null
}

export function RsvpList() {
  const { tableProps } = useTable<Rsvp>({
    syncWithLocation: true,
  })

  // Resolve user_id -> name for ONLY the attendees on the current page. Derive
  // the ids from the page's rows and fetch just those profiles (IN filter), so
  // we never pull the whole profiles table to build the name map.
  const userIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          ((tableProps.dataSource ?? []) as Rsvp[])
            .map((r) => r.user_id)
            .filter((v): v is string => Boolean(v)),
        ),
      ),
    [tableProps.dataSource],
  )
  const { result: profileResult } = useList<Profile>({
    resource: 'profiles',
    filters: userIds.length ? [{ field: 'user_id', operator: 'in', value: userIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: userIds.length > 0 },
  })

  const profileMap = React.useMemo<Record<string, string>>(() => {
    const items = (profileResult?.data ?? []) as Profile[]
    return Object.fromEntries(
      items
        .filter((p) => p.user_id != null)
        .map((p) => [p.user_id as string, p.name]),
    )
  }, [profileResult])

  // Resolve event_id -> title for ONLY the events referenced on the current page
  // (scoped IN lookup, same bounded pattern as the attendee names), so the table
  // shows WHICH event each RSVP is for instead of a bare row.
  const eventIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          ((tableProps.dataSource ?? []) as Rsvp[])
            .map((r) => r.event_id)
            .filter((v): v is string => Boolean(v)),
        ),
      ),
    [tableProps.dataSource],
  )
  const { result: eventResult } = useList<Event>({
    resource: 'events',
    filters: eventIds.length ? [{ field: 'id', operator: 'in', value: eventIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: eventIds.length > 0 },
  })
  const eventMap = React.useMemo<Record<string, string>>(() => {
    const items = (eventResult?.data ?? []) as Event[]
    return Object.fromEntries(items.filter((e) => e.id != null).map((e) => [e.id as string, e.title]))
  }, [eventResult])

  // Row click → the RSVP show page.
  const { show } = useNavigation()

  // Summary deck — read the precomputed status counts + guest sum straight off
  // the one-row admin summary view instead of fetching every RSVP and reducing
  // on the main thread.
  const { result: summaryRes } = useList<RsvpsSummary>({
    resource: 'admin_rsvps_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]

  const stats: Stat[] = [
    { label: 'Confirmed', value: (summary?.confirmed_count ?? 0).toLocaleString(), icon: CalendarCheck, tone: 'green' },
    { label: 'Pending', value: (summary?.pending_count ?? 0).toLocaleString(), icon: CalendarDays, tone: 'gold' },
    { label: 'Waitlisted', value: (summary?.waitlisted_count ?? 0).toLocaleString(), icon: Ticket, tone: 'jaguar' },
    { label: 'Total Guests', value: (summary?.total_guests ?? 0).toLocaleString(), icon: UsersRound, tone: 'gold' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Events · RSVPs',
        description:
          'Who is coming, who they are bringing, and what they will eat. Track confirmations and the waitlist so the team can plan seating, meals and head counts with confidence.',
      }}
      stats={stats}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table
            {...tableProps}
            rowKey="id"
            scroll={{ x: true }}
            onRow={(record: Rsvp) => ({
              onClick: () => show('rsvps', record.id),
              style: { cursor: 'pointer' },
            })}
          >
            <Table.Column<Rsvp>
              dataIndex="user_id"
              title="Attendee"
              render={(userId: string) => renderAvatarName(profileMap[userId] ?? userId)}
            />
            <Table.Column<Rsvp>
              dataIndex="event_id"
              title="Event"
              render={(eventId: string | null) =>
                eventId ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>
                    {eventMap[eventId] ?? eventId}
                  </span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Rsvp>
              dataIndex="guests"
              title="Guests"
              sorter
              render={(g: number) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 700, color: brand.ink }}>{String(g)}</span>
              )}
            />
            <Table.Column<Rsvp>
              dataIndex="meal"
              title="Meal"
              render={(meal: string | null) =>
                meal ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.sub }}>{meal}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Rsvp> dataIndex="status" title="Status" render={renderStatus} />
            <Table.Column<Rsvp>
              dataIndex="created_at"
              title="Created"
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Rsvp>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Rsvp) => (
                // Stop the row-click navigation when the per-row action buttons are used.
                // No DeleteButton: RLS grants admins UPDATE on rsvps but no DELETE
                // policy, so a delete would be a silent no-op. Move an RSVP to
                // "cancelled" via Edit instead.
                <Space onClick={(e) => e.stopPropagation()}>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
